/**
 * i18next-backed label maps for enum display strings.
 * Proxies and helpers work outside React (stores, utils, non-hook call sites).
 */

import i18n from '@/i18n/i18n';

function label(section: string, key: string, defaultValue?: string): string {
  return i18n.t(`labels:${section}.${key}`, { defaultValue: defaultValue ?? key });
}

function createLabelProxy<K extends string>(section: string, keys: readonly K[]): Record<K, string> {
  return new Proxy({} as Record<K, string>, {
    get(_target, prop: string | symbol) {
      if (typeof prop !== 'string') return undefined;
      return label(section, prop);
    },
    ownKeys() {
      return [...keys];
    },
    getOwnPropertyDescriptor(_target, prop) {
      if (typeof prop === 'string' && (keys as readonly string[]).includes(prop)) {
        return { enumerable: true, configurable: true };
      }
      return undefined;
    }
  });
}

const SOURCE_TYPE_KEYS = ['person', 'website', 'publication', 'repository', 'other'] as const;
const PARENT_CHILD_KEYS = ['biological', 'adoptive', 'foster', 'step', 'guardian'] as const;
const PET_OWNER_ROLE_KEYS = ['primary', 'secondary', 'caregiver'] as const;
const PET_SUBTYPE_KEYS = [
  'dog',
  'cat',
  'mammal',
  'equine',
  'bird',
  'aquatic',
  'reptile',
  'amphibian',
  'invertebrate',
  'exotic',
  'other'
] as const;
type RoleKey = 'owner' | 'admin' | 'editor' | 'contributor' | 'viewer';
type NotificationCadenceKey = 'off' | 'daily' | 'weekly' | 'monthly';
type ActivityCategoryKey = 'all' | 'people' | 'memories' | 'media' | 'invites' | 'tree' | 'imports';
type ActivityTargetTypeKey =
  | 'family_member'
  | 'life_event'
  | 'person_fact'
  | 'person'
  | 'pet'
  | 'story'
  | 'media_asset'
  | 'media_export_job'
  | 'gedcom_export'
  | 'invitation'
  | 'user'
  | 'relationship'
  | 'import_job'
  | 'family'
  | 'source'
  | 'citation';

export function getSourceTypeLabel(key: (typeof SOURCE_TYPE_KEYS)[number]): string {
  return label('sourceType', key);
}

export function getParentChildLabel(key: (typeof PARENT_CHILD_KEYS)[number]): string {
  return label('parentChild', key);
}

export function getPetOwnerRoleLabel(key: (typeof PET_OWNER_ROLE_KEYS)[number]): string {
  return label('petOwnerRole', key);
}

export function getPetSubtypeLabel(key: (typeof PET_SUBTYPE_KEYS)[number]): string {
  return label('petSubtype', key);
}

export function getDiffPathLabel(path: string): string {
  return i18n.t(`labels:diffPath.${path}`, { defaultValue: path });
}

export function getRoleLabel(key: RoleKey): string {
  return label('role', key);
}

export function getNotificationCadenceLabel(key: NotificationCadenceKey): string {
  return label('notificationCadence', key);
}

export function getActivityCategoryLabel(key: ActivityCategoryKey): string {
  return label('activityCategory', key);
}

export function getActivityTargetTypeLabel(key: ActivityTargetTypeKey): string {
  return label('activityTargetType', key);
}

/** @deprecated Prefer getSourceTypeLabel; kept for existing imports. */
export const SOURCE_TYPE_LABELS = createLabelProxy('sourceType', SOURCE_TYPE_KEYS);

/** @deprecated Prefer getParentChildLabel; kept for existing imports. */
export const PARENT_CHILD_LABELS = createLabelProxy('parentChild', PARENT_CHILD_KEYS);

/** @deprecated Prefer getPetOwnerRoleLabel; kept for existing imports. */
export const PET_OWNER_ROLE_LABELS = createLabelProxy('petOwnerRole', PET_OWNER_ROLE_KEYS);

/** @deprecated Prefer getPetSubtypeLabel; kept for existing imports. */
export const PET_SUBTYPE_LABEL = createLabelProxy('petSubtype', PET_SUBTYPE_KEYS);
